'use client';

import { StageProgress, Table, Textarea } from '@/layers/presentation/components/ui';
import { useDocumentParser } from '@/layers/presentation/context/DocumentParserContext';
import { UserType } from '@/types/modelEnums';
import {
  Button,
  ButtonGroup,
  Listbox,
  ListboxItem,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Tab,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
  Tabs,
} from '@nextui-org/react';
import { AlertCircle, CheckCircle, FileText, Loader2, UploadCloud, XCircle } from 'lucide-react';
import { useRouter } from 'next/navigation';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDropzone } from 'react-dropzone';

interface MedicalDocParserProps {
  userId?: string;
  onApproveParams?: (params: {
    cancerType: string | null;
    cancerTypeId: number | null;
    cancerSubtypeId: number | null;
    genderId: number | null;
    ageId: number | null;
    additionalInfo?: string | null;
  }) => void;
  approveButtonText?: string;
  userType?: UserType;
  currentAdditionalInfo?: string;
}

export function MedicalDocParser({
  userId,
  onApproveParams,
  approveButtonText = 'Approve and Save',
  userType,
  currentAdditionalInfo,
}: MedicalDocParserProps) {
  const router = useRouter();

  // Use the new DocumentParserContext
  const {
    currentJobId,
    stages,
    error,
    uiState,
    isProcessing,
    activeTab,
    setActiveTab,
    initiateProcessing,
    closeParser,
    minimizeParser,
    extractedData,
    setExtractedData,
    clearJob,
    uploadedFile,
    setUploadedFile,
    sourceRoute,
    sourceType,
    sourceText,
    setSourceType,
    setSourceText,
  } = useDocumentParser();

  // Local state
  const [timeElapsed, setTimeElapsed] = useState(0);

  // Timer for processing
  useEffect(() => {
    let timer: NodeJS.Timeout | null = null;

    if (isProcessing) {
      setTimeElapsed(0);
      timer = setInterval(() => {
        setTimeElapsed(prev => prev + 1);
      }, 1000);
    } else if (timer) {
      clearInterval(timer);
    }

    return () => {
      if (timer) clearInterval(timer);
    };
  }, [isProcessing]);

  // Handle file upload
  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      if (acceptedFiles.length === 0) return;

      const file = acceptedFiles[0];
      setUploadedFile(file);

      try {
        await initiateProcessing(file, null, currentAdditionalInfo);
      } catch (error) {
        console.error('Failed to process file:', error);
      }
    },
    [initiateProcessing, setUploadedFile, currentAdditionalInfo],
  );

  // Dropzone configuration
  const dropzoneConfig = useMemo(
    () => ({
      onDrop,
      accept: {
        'application/pdf': ['.pdf'],
      },
      maxFiles: 1,
      maxSize: 5 * 1024 * 1024, // 5MB
      disabled: isProcessing,
    }),
    [onDrop, isProcessing],
  );

  // Use dropzone hook with the config
  const { getRootProps, getInputProps, isDragActive } = useDropzone(dropzoneConfig);

  // Map between UI tab keys (lowercase) and context values (uppercase)
  const handleTabChange = useCallback(
    (key: any) => {
      const contextTabValue = key === 'upload' ? 'UPLOAD' : 'REVIEW';
      setActiveTab(contextTabValue);
    },
    [setActiveTab],
  );

  // Map from context values to UI values
  const currentTabKey = useMemo(() => {
    return activeTab === 'UPLOAD' ? 'upload' : 'review';
  }, [activeTab]);

  const handleApprove = useCallback(() => {
    if (extractedData?.patientParameters && !onApproveParams) {
      router.push(sourceRoute || `/${userType}`);
    }

    if (extractedData?.patientParameters && onApproveParams) {
      onApproveParams({
        cancerTypeId: extractedData.patientParameters.cancerTypeId
          ? Number(extractedData.patientParameters.cancerTypeId)
          : null,
        cancerSubtypeId: extractedData.patientParameters.cancerSubtypeId
          ? Number(extractedData.patientParameters.cancerSubtypeId)
          : null,
        genderId: extractedData.patientParameters.genderId
          ? Number(extractedData.patientParameters.genderId)
          : null,
        ageId: extractedData.patientParameters.ageId
          ? Number(extractedData.patientParameters.ageId)
          : null,
        additionalInfo: extractedData.patientParameters.additionalInfo,
        cancerType: extractedData.patientParameters.cancerType || null,
      });

      // Clean up in all cases
      clearJob();
      closeParser();
      setActiveTab('UPLOAD');
      setUploadedFile(null);
    }
  }, [
    userType,
    extractedData,
    onApproveParams,
    closeParser,
    router,
    clearJob,
    setActiveTab,
    setUploadedFile,
    sourceRoute,
  ]);

  // Reset the parser to initial state
  const handleReset = useCallback(() => {
    setActiveTab('UPLOAD');
    setUploadedFile(null);
    setExtractedData(null);
    setTimeElapsed(0);
    clearJob();
  }, [setActiveTab, setExtractedData, clearJob, setUploadedFile]);

  // Map job statuses to component states
  const isLoading = isProcessing;
  const isApproveButtonDisabled = !extractedData?.patientParameters;

  const renderUploadTab = () => (
    <div className="w-full h-full max-h-[60vh] overflow-y-auto">
      {/* File Upload Area - Only show when not processing */}
      {!isLoading ? (
        <div>
          <p className="mb-6">
            We don&apos;t keep any of your documents and personal information. We only use imported
            document to extract anonymized medical data.
          </p>
          <ButtonGroup className="mb-4" aria-label="Select import method">
            <Button
              variant={sourceType === 'text' ? 'solid' : 'bordered'}
              color="default"
              onPress={() => setSourceType('text')}
            >
              Import Text
            </Button>
            <Button
              variant={sourceType === 'upload' ? 'solid' : 'bordered'}
              color="default"
              onPress={() => setSourceType('upload')}
            >
              Upload PDF
            </Button>
          </ButtonGroup>
          {sourceType === 'text' && (
            <div className="flex flex-col gap-4">
              <div>
                <Textarea
                  placeholder="Paste medical document text or patient note here..."
                  value={sourceText || ''}
                  onChange={e => setSourceText(e.target.value)}
                  className="w-full h-20"
                  maxRows={3}
                  aria-label="Medical document text"
                />
              </div>
              <Button
                color="primary"
                onPress={async () => {
                  if (!sourceText?.trim()) {
                    // You could set an error state here if you have one
                    console.error('No text to process');
                    return;
                  }

                  // Client-side validation
                  const textSizeBytes = new Blob([sourceText]).size;
                  if (textSizeBytes > 5 * 1024 * 1024) {
                    // 5MB limit
                    console.error('Text too large');
                    // You could set an error state here
                    return;
                  }

                  try {
                    await initiateProcessing(null, sourceText, currentAdditionalInfo);
                  } catch (error) {
                    console.error('Failed to initiate processing:', error);
                  }
                }}
                isDisabled={!sourceText?.trim()}
              >
                Import and Start Processing
              </Button>
            </div>
          )}
          {sourceType === 'upload' && (
            <div
              {...getRootProps()}
              className={`p-6 border-2 border-dashed rounded-xl cursor-pointer text-center transition-colors
                         ${isDragActive ? 'border-primary bg-primary-50' : 'border-default-900 hover:bg-default-200'}`}
            >
              <input {...getInputProps()} />
              <UploadCloud className="mx-auto h-12 w-12 text-default-400 mb-2" />
              {isDragActive ? (
                <p className="text-primary">Drop the PDF file here...</p>
              ) : (
                <p className="text-default-500">
                  Drag &apos;n&apos; drop a PDF file here, or click to select
                </p>
              )}
              {uploadedFile && (
                <p className="mt-2 text-sm text-success-600 flex items-center justify-center">
                  <FileText size={16} className="mr-1" />
                  {uploadedFile.name} selected.
                </p>
              )}
            </div>
          )}
        </div>
      ) : (
        <div className="text-center">
          <FileText size={24} className="mx-auto text-primary mb-2" />
          <p className="text-primary font-medium">Processing {uploadedFile?.name}</p>
          <p className="text-sm text-default-500 mt-1">
            Please wait while we analyze your document
          </p>
        </div>
      )}

      {/* Progress Indicator - Show if stages exist */}
      {isProcessing && (
        <div className="w-full text-center pt-4 space-y-4">
          {/* Stage Progress Bar */}
          {isLoading && <StageProgress stages={stages} className="mx-auto mt-2" />}

          {/* Stages List */}
          <Listbox
            aria-label="Document processing stages"
            variant="flat"
            className="bg-default-100 rounded-lg mt-4"
          >
            {Object.values(stages).map(stage => (
              <ListboxItem
                key={stage.id}
                textValue={stage.name}
                startContent={
                  stage.status === 'COMPLETE' ? (
                    <CheckCircle size={18} className="text-success" />
                  ) : stage.status === 'IN_PROGRESS' ? (
                    <Loader2 size={18} className="animate-spin text-primary" />
                  ) : stage.status === 'FAILED' ? (
                    <XCircle size={18} className="text-danger" />
                  ) : (
                    <span className="w-[18px] h-[18px] inline-block"></span> // Placeholder for alignment
                  )
                }
              >
                <div className="flex flex-col">
                  <span>{stage.name}</span>
                  {stage.status === 'IN_PROGRESS' && stage.message && (
                    <span className="ml-2 text-xs text-default-500">{stage.message}</span>
                  )}
                  {stage.status === 'FAILED' && stage.message && (
                    <span className="ml-2 text-xs text-danger">{stage.message}</span>
                  )}
                </div>
              </ListboxItem>
            ))}
          </Listbox>
          {/* Format time elapsed as MM:SS */}
          <p className="text-sm text-default-500">
            Processing takes 5 minutes maximum. Time elapsed: {Math.floor(timeElapsed / 60)}:
            {(timeElapsed % 60).toString().padStart(2, '0')}.<br />
            <br />
            You can close this window and continue using Right2Hope.
          </p>
        </div>
      )}

      {/* Error Display */}
      {error && !isLoading && (
        <div className="mt-4 text-center p-4 bg-danger-50 border border-danger-200 rounded-lg text-danger-600 flex items-center justify-center">
          <AlertCircle size={20} className="mr-2" /> {error}
        </div>
      )}
    </div>
  );

  const renderReviewTab = () => {
    // Only create tableRows if extractedData and patientParameters exist
    const tableRows = extractedData?.patientParameters
      ? [
          <TableRow key="cancer-type">
            <TableCell>Cancer type</TableCell>
            <TableCell>{extractedData.patientParameters.cancerType || '-'}</TableCell>
          </TableRow>,
          <TableRow key="cancer-subtype">
            <TableCell>Cancer subtype</TableCell>
            <TableCell>{extractedData.patientParameters.cancerSubtype || '-'}</TableCell>
          </TableRow>,
          <TableRow key="gender">
            <TableCell>Gender</TableCell>
            <TableCell>{extractedData.patientParameters.gender || '-'}</TableCell>
          </TableRow>,
          <TableRow key="age">
            <TableCell>Age</TableCell>
            <TableCell>{extractedData.patientParameters.age || '-'}</TableCell>
          </TableRow>,
        ]
      : [];

    // Add additional info row conditionally
    if (extractedData?.patientParameters?.additionalInfo) {
      tableRows.push(
        <TableRow key="additional-info">
          <TableCell>Additional information</TableCell>
          <TableCell className="whitespace-pre-wrap max-w-md">
            {typeof extractedData.patientParameters.additionalInfo === 'string'
              ? extractedData.patientParameters.additionalInfo
              : JSON.stringify(extractedData.patientParameters.additionalInfo)}
          </TableCell>
        </TableRow>,
      );
    }

    return (
      <div className="w-full h-full max-h-[60vh] overflow-y-auto">
        {extractedData && (
          <div className="space-y-4">
            {extractedData.patientParameters && (
              <div className="flex flex-col gap-4">
                <Table removeWrapper aria-label="Extracted medical data">
                  <TableHeader className="hide">
                    <TableColumn>Parameter</TableColumn>
                    <TableColumn>Value</TableColumn>
                  </TableHeader>
                  <TableBody>{tableRows}</TableBody>
                </Table>
              </div>
            )}
          </div>
        )}
        {!extractedData && (
          <div className="text-center p-6">
            <p className="text-default-500">Please import and process a medical document first.</p>
          </div>
        )}
      </div>
    );
  };

  const handleCloseModal = () => {
    // Instead of closing the parser, minimize it
    minimizeParser();
  };

  return (
    <div>
      <Modal
        isOpen={uiState === 'FULL'}
        onClose={handleCloseModal}
        size="3xl"
        scrollBehavior="inside"
        classNames={{
          base: 'max-h-[90vh]',
          body: 'flex-1 overflow-hidden',
        }}
      >
        <ModalContent className="flex flex-col h-full">
          <ModalHeader>Import Medical Data</ModalHeader>
          <ModalBody className="pb-6 flex flex-col flex-1 overflow-hidden">
            <Tabs
              selectedKey={currentTabKey}
              onSelectionChange={handleTabChange}
              variant="underlined"
              color="primary"
              aria-label="Medical document import steps"
              classNames={{
                tab: 'px-4 py-2',
                tabContent: 'flex-1 overflow-hidden',
                panel: 'h-full',
              }}
              className="flex flex-col flex-1"
            >
              <Tab key="upload" title="1. Import">
                {renderUploadTab()}
              </Tab>
              <Tab key="review" title="2. Review & Approve" isDisabled={!extractedData}>
                {renderReviewTab()}
              </Tab>
            </Tabs>
          </ModalBody>
          <ModalFooter className="flex justify-between">
            {currentJobId && !isProcessing && (
              <Button color="default" variant="bordered" radius="md" onClick={handleReset}>
                {isProcessing ? 'Cancel and Reset' : 'Reset'}
              </Button>
            )}
            {extractedData && extractedData.patientParameters && (
              <Button color="primary" onClick={handleApprove} isDisabled={isApproveButtonDisabled}>
                {approveButtonText}
              </Button>
            )}
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  );
}
